import { Box, Button, FormControl, FormLabel, Input } from '@chakra-ui/react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { FileDropzone } from '../../../components/FileInput'
import { MaskedInput, MoneyInput, PIX_KEY_MASKS } from '../../../components/MaskedInput'
import PublicFormLayout from '../../../components/publicFormLayout/PublicFormLayout'
import { FormSelect } from '../../../components/Select'
import { formatCurrencyValue, PixKeyType, useAppToast } from '../../../helpers'
import { IQuestion, IRefundsAnswer } from '../../../services/Refunds/interfaces'
import RefundsService from '../../../services/Refunds/RefundsService'

/** As respostas de arquivo viajam em base64 no corpo do formulário, que já é grande. */
const REFUND_FILE_MAX_MB = 5

/** Id fixo da pergunta "Valor Total da Despesa" no formulário de reembolso. */
const EXPENSE_VALUE_QUESTION_ID = 4
/** Id fixo da pergunta "Tipo de Chave PIX". */
const PIX_KEY_TYPE_QUESTION_ID = 5
/** Id fixo da pergunta "Chave PIX para Reembolso". */
const PIX_KEY_QUESTION_ID = 6

/** O formulário é dirigido por dados: a resposta do tipo chega como o rótulo em pt-BR. */
const PIX_KEY_TYPES: Record<string, PixKeyType> = {
  'CPF/CNPJ': 'cpf_cnpj',
  Telefone: 'phone',
  'Chave Aleatória': 'random'
}

/**
 * A resposta desta pergunta vai para a API como a string formatada (`'R$ 1.234,50'`), que é o que
 * o backend guarda — daí a ponte entre o número do `<MoneyInput>` e o texto da resposta.
 */
const expenseValueToNumber = (answer: string | string[] | undefined): number | null => {
  const digits = String(answer ?? '').replace(/\D/g, '')
  return digits === '' ? null : Number(digits) / 100
}

export default function RefundsStepPage() {
  const navigate = useNavigate()
  const toast = useAppToast()
  const [saving, setSaving] = useState(false)
  const refundsService = useMemo(() => new RefundsService(), [])
  const [formAnswers, setFormAnswers] = useState<IRefundsAnswer>({})
  const [questionFiles, setQuestionFiles] = useState<Record<number, File[]>>({})
  /** Qual seleção de cada pergunta é a corrente, para descartar conversão que terminou atrasada. */
  const selectionRef = useRef<Record<number, number>>({})
  const { id } = useParams()
  const [questions, setQuestions] = useState<IQuestion[]>([])

  const fetchQuestions = async () => {
    try {
      const response = await refundsService.getQuestions()
      setQuestions(response.data)
    } catch {
      toast.error('Erro ao buscar as perguntas para a Solicitação de Reembolso!')
    }
  }

  /**
   * A resposta de arquivo vai para a API em base64, então a página guarda as duas formas: os
   * `File`, que é o que o campo mostra, e o base64, que é o que é enviado.
   *
   * A versão anterior validava tipo e tamanho dentro de um `Promise.all` e rejeitava a promessa do
   * arquivo recusado — como o `catch` era vazio, **os arquivos válidos sumiam junto com o
   * inválido**, e a única pista era o toast do recusado. A validação agora é do campo, arquivo a
   * arquivo.
   *
   * O que sobrou aqui é a conversão, e ela é assíncrona: trocar a seleção enquanto o `FileReader`
   * anterior ainda lê faz as duas terminarem fora de ordem, e a antiga grava por último. O
   * formulário enviaria arquivos diferentes dos que estão na tela — num pedido de reembolso, o
   * comprovante errado. O contador marca qual seleção é a corrente e só ela pode gravar.
   */
  const handleFilesChange = async (questionId: number, files: File[]) => {
    setQuestionFiles((prev) => ({ ...prev, [questionId]: files }))

    const selectionId = (selectionRef.current[questionId] ?? 0) + 1
    selectionRef.current[questionId] = selectionId

    try {
      const base64Files = await Promise.all(
        files.map(
          (file) =>
            new Promise<string>((resolve, reject) => {
              const reader = new FileReader()
              reader.onloadend = () => resolve(reader.result as string)
              reader.onerror = () => reject(reader.error)
              reader.readAsDataURL(file)
            })
        )
      )

      if (selectionRef.current[questionId] !== selectionId) return

      setFormAnswers((prev) => ({ ...prev, [questionId]: base64Files }))
    } catch {
      if (selectionRef.current[questionId] !== selectionId) return

      // Sem isto o campo ficava mostrando os arquivos e a resposta seguia com o valor anterior —
      // a mesma divergência entre tela e payload, por outro caminho.
      setQuestionFiles((prev) => ({ ...prev, [questionId]: [] }))
      setFormAnswers((prev) => ({ ...prev, [questionId]: [] }))
      toast.error('Erro ao ler os arquivos', 'Escolha os arquivos novamente.')
    }
  }

  /** A máscara da chave depende do tipo respondido na pergunta anterior. */
  const pixKeyMask = () => {
    const pixKeyTypeQuestion = questions.find((q) => q.id === PIX_KEY_TYPE_QUESTION_ID)
    const answeredType = pixKeyTypeQuestion ? PIX_KEY_TYPES[String(formAnswers[pixKeyTypeQuestion.id])] : undefined

    return answeredType ? PIX_KEY_MASKS[answeredType] : null
  }

  useEffect(() => {
    fetchQuestions()
  }, [])

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setSaving(true)

    const normalizedAnswers: IRefundsAnswer = { ...formAnswers }
    questions.forEach((question) => {
      if (question.type === 'file') {
        if (!Array.isArray(normalizedAnswers[question.id])) {
          normalizedAnswers[question.id] = []
        }
      }
    })

    const hasEmptyField = questions.some((question) => {
      if (isHiddenQuestion(question)) {
        return false
      }
      if (question.type === 'file') {
        return !normalizedAnswers[question.id] || normalizedAnswers[question.id].length === 0
      }
      return !normalizedAnswers[question.id]
    })

    if (hasEmptyField) {
      toast.warning('Preencha todos os campos')
      setSaving(false)
      return
    }

    try {
      const response = await refundsService.storeAnswers(Number(id), [normalizedAnswers])
      if (response.data.success) {
        navigate(`/refunds/complete`)
      }
    } catch {
      toast.error('Erro ao salvar respostas da Solicitação de Reembolso!')
    } finally {
      setSaving(false)
    }
  }

  const isHiddenQuestion = (question: IQuestion): boolean => {
    switch (question.id) {
      case EXPENSE_VALUE_QUESTION_ID: // Valor Total da Despesa
        if (formAnswers[1] === 'KMs Rodados') return true // Tipo de Despesa === 'KMs Rodados'
        break
      default:
        break
    }
    return false
  }

  return (
    <PublicFormLayout title="Solicitação de Reembolso">
      <form onSubmit={handleSubmit}>
        {questions.map((question) => {
          if (isHiddenQuestion(question)) return null

          // O campo de arquivo desenha o próprio `FormControl`, então fica fora — um `FormControl`
          // dentro de outro não associa rótulo nem erro a coisa nenhuma.
          if (question.type === 'file') {
            return (
              <FileDropzone
                key={question.id}
                accept="receipt"
                maxSizeMB={REFUND_FILE_MAX_MB}
                label={question.label}
                isRequired
                preview="thumbnail"
                mb="4"
                files={questionFiles[question.id] ?? []}
                onChange={(files) => handleFilesChange(question.id, files)}
              />
            )
          }

          return (
            <FormControl isRequired={true} key={question.id} mb="4">
              <FormLabel>{question.label}</FormLabel>
              {question.type === 'text' && question.id === EXPENSE_VALUE_QUESTION_ID && (
                <MoneyInput
                  value={expenseValueToNumber(formAnswers[question.id])}
                  onChange={(value) =>
                    setFormAnswers({
                      ...formAnswers,
                      [question.id]: value == null ? '' : `R$ ${formatCurrencyValue(value)}`
                    })
                  }
                />
              )}
              {question.type === 'text' && question.id === PIX_KEY_QUESTION_ID && (
                <MaskedInput
                  mask={pixKeyMask()}
                  value={String(formAnswers[question.id] ?? '')}
                  onChange={(pixKey) => setFormAnswers({ ...formAnswers, [question.id]: pixKey })}
                />
              )}
              {question.type === 'text' &&
                question.id !== EXPENSE_VALUE_QUESTION_ID &&
                question.id !== PIX_KEY_QUESTION_ID && (
                  <Input
                    value={formAnswers[question.id]}
                    onChange={(event) => setFormAnswers({ ...formAnswers, [question.id]: event.target.value })}
                  />
                )}
              {question.type === 'select' && (
                <FormSelect
                  value={String(formAnswers[question.id] ?? '')}
                  onChange={(value) => setFormAnswers({ ...formAnswers, [question.id]: value })}
                  options={(question.options ?? []).map((option) => ({ value: option, label: option }))}
                  placeholder="Selecione uma opção"
                />
              )}
              {question.type === 'options' && (
                <Box display="flex" flexDirection="row" flexWrap="wrap" gap={2}>
                  {question.options &&
                    question.options.map((option, index) => (
                      <Button
                        key={index}
                        onClick={() => setFormAnswers({ ...formAnswers, [question.id]: option })}
                        colorScheme={formAnswers[question.id] === option ? 'magenta' : 'gray'}
                        variant={formAnswers[question.id] === option ? 'solid' : 'outline'}
                      >
                        {option}
                      </Button>
                    ))}
                </Box>
              )}
            </FormControl>
          )
        })}
        <Box display={'flex'}>
          <Button colorScheme="magenta" disabled={saving} type="submit">
            Enviar
          </Button>
        </Box>
      </form>
    </PublicFormLayout>
  )
}
